import { format, formatDistance, formatRelative } from 'date-fns'
import { CURRENCY, DATE_FORMATS } from './constants'

// Format currency
export const formatCurrency = (amount, options = {}) => {
  const {
    showSymbol = true,
    decimals = 2,
    locale = CURRENCY.LOCALE
  } = options

  const formatted = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: CURRENCY.CODE,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(amount)

  return showSymbol ? formatted : formatted.replace(CURRENCY.SYMBOL, '').trim()
}

// Format large numbers with K, M, B suffixes
export const formatCompactNumber = (num) => {
  if (num >= 1000000000) {
    return (num / 1000000000).toFixed(1) + 'B'
  }
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M'
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K'
  }
  return num.toString()
}

// Format date
export const formatDate = (date, formatStr = DATE_FORMATS.FULL) => {
  if (!date) return '-'
  try {
    return format(new Date(date), formatStr)
  } catch (error) {
    console.error('Error formatting date:', error)
    return '-'
  }
}

// Format relative time (e.g., "2 hours ago")
export const formatRelativeTime = (date) => {
  if (!date) return '-'
  try {
    return formatDistance(new Date(date), new Date(), { addSuffix: true })
  } catch (error) {
    console.error('Error formatting relative time:', error)
    return '-'
  }
}

// Format relative date (e.g., "Today at 3:00 PM")
export const formatRelativeDate = (date) => {
  if (!date) return '-'
  try {
    return formatRelative(new Date(date), new Date())
  } catch (error) {
    console.error('Error formatting relative date:', error)
    return '-'
  }
}

// Format phone number
export const formatPhoneNumber = (phone) => {
  if (!phone) return '-'
  const cleaned = phone.replace(/\D/g, '')
  const match = cleaned.match(/^(\d{1,3})(\d{3})(\d{3})(\d{4})$/)
  if (match) {
    return `+${match[1]} (${match[2]}) ${match[3]}-${match[4]}`
  }
  return phone
}

// Format percentage
export const formatPercentage = (value, decimals = 2) => {
  return `${value.toFixed(decimals)}%`
}

// Truncate text
export const truncateText = (text, maxLength = 50) => {
  if (!text) return ''
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

// Format transaction type
export const formatTransactionType = (type) => {
  const types = {
    deposit: 'Deposit',
    withdrawal: 'Withdrawal',
    transfer_in: 'Transfer In',
    transfer_out: 'Transfer Out',
    admin_adjustment: 'Admin Adjustment'
  }
  return types[type] || type
}

// Format transaction status
export const formatTransactionStatus = (status) => {
  const statuses = {
    pending: 'Pending',
    approved: 'Approved',
    rejected: 'Rejected',
    completed: 'Completed'
  }
  return statuses[status] || status
}

// Format account status
export const formatAccountStatus = (status) => {
  const statuses = {
    active: 'Active',
    suspended: 'Suspended',
    closed: 'Closed'
  }
  return statuses[status] || status
}

// Format file size
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
}

// Get initials from name
export const getInitials = (name) => {
  if (!name) return '?'
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .substring(0, 2)
}

// Generate random color from string (for avatars)
export const stringToColor = (str) => {
  if (!str) return '#64748b'
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
  }
  const c = (hash & 0x00FFFFFF).toString(16).toUpperCase()
  return '#' + '00000'.substring(0, 6 - c.length) + c
}

// Capitalize first letter
export const capitalize = (str) => {
  if (!str) return ''
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

// Format card number (mask)
export const formatCardNumber = (number) => {
  if (!number) return ''
  return '**** **** **** ' + number.slice(-4)
}

// Parse query string
export const parseQueryString = (queryString) => {
  const params = new URLSearchParams(queryString)
  const result = {}
  for (const [key, value] of params) {
    result[key] = value
  }
  return result
}

// Build query string
export const buildQueryString = (params) => {
  return Object.keys(params)
    .filter(key => params[key] !== undefined && params[key] !== null)
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
    .join('&')
}

// Deep clone object
export const deepClone = (obj) => {
  return JSON.parse(JSON.stringify(obj))
}

// Debounce function
export const debounce = (func, wait = 300) => {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

// Throttle function
export const throttle = (func, limit = 300) => {
  let inThrottle
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args)
      inThrottle = true
      setTimeout(() => inThrottle = false, limit)
    }
  }
}

// Check if object is empty
export const isEmpty = (obj) => {
  return obj && Object.keys(obj).length === 0 && obj.constructor === Object
}

// Sleep/delay function
export const sleep = (ms) => {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export default {
  formatCurrency,
  formatCompactNumber,
  formatDate,
  formatRelativeTime,
  formatRelativeDate,
  formatPhoneNumber,
  formatPercentage,
  truncateText,
  formatTransactionType,
  formatTransactionStatus,
  formatAccountStatus,
  formatFileSize,
  getInitials,
  stringToColor,
  capitalize,
  formatCardNumber,
  parseQueryString,
  buildQueryString,
  deepClone,
  debounce,
  throttle,
  isEmpty,
  sleep
}